import Phaser from 'phaser';
import {
  findBackground,
  findTrack,
  projectPictureKey,
  type CreatorProject,
} from '../../config/creator';
import { drawTrailStyle } from '../trackDraw';
import { PIECE_DEPTH, makePieceImage } from './pieces';

/**
 * Shared look for the Level Creator's screens (spec 2026-11-01): the EXPLORE
 * tab's teal, one button recipe, a header with BACK.
 */
export const CREATOR_TEAL = 0x2fb8a6;
export const CREATOR_TEAL_HEX = '#2fb8a6';
export const CREATOR_ORANGE = 0xf08a24;
export const CREATOR_PANEL = 0x15131f;
export const CREATOR_INK = '#e8f4f1';

export interface ButtonOpts {
  name: string;
  fill?: number;
  color?: string;
  fontSize?: number;
  width?: number;
  height?: number;
  depth?: number;
}

/** A filled rounded-ish button: a rectangle with a centred label. */
export function creatorButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  opts: ButtonOpts,
  onUp: () => void,
): { bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text } {
  const fill = opts.fill ?? CREATOR_TEAL;
  const depth = opts.depth ?? 10;
  const text = scene.add
    .text(x, y, label, {
      fontSize: `${opts.fontSize ?? 22}px`,
      color: opts.color ?? '#ffffff',
      fontStyle: 'bold',
    })
    .setOrigin(0.5)
    .setDepth(depth + 1);
  const w = opts.width ?? Math.max(120, text.width + 40);
  const h = opts.height ?? 52;
  const bg = scene.add
    .rectangle(x, y, w, h, fill, 1)
    .setStrokeStyle(2, 0xffffff, 0.35)
    .setDepth(depth)
    .setInteractive({ useHandCursor: true })
    .setName(opts.name);
  bg.on('pointerover', () => bg.setStrokeStyle(3, 0xffffff, 0.9));
  bg.on('pointerout', () => bg.setStrokeStyle(2, 0xffffff, 0.35));
  bg.on('pointerup', onUp);
  return { bg, text };
}

/** The screen title and a ← BACK button top-left. */
export function creatorHeader(scene: Phaser.Scene, title: string, onBack: () => void): void {
  scene.add
    .text(640, 48, title, { fontSize: '36px', color: CREATOR_TEAL_HEX, fontStyle: 'bold' })
    .setOrigin(0.5)
    .setLetterSpacing(2)
    .setDepth(10);
  const back = scene.add
    .text(80, 50, '← BACK', {
      fontSize: '22px',
      color: '#cfe0bd',
      backgroundColor: '#15131f',
      padding: { x: 16, y: 12 },
    })
    .setOrigin(0.5)
    .setDepth(10)
    .setName('creator-back')
    .setInteractive({ useHandCursor: true });
  back.on('pointerover', () => back.setColor(CREATOR_TEAL_HEX));
  back.on('pointerout', () => back.setColor('#cfe0bd'));
  back.on('pointerup', onBack);
}

/** A short-lived message in the middle of the screen. */
export function creatorFlash(scene: Phaser.Scene, text: string, y = 360, color = '#ffffff'): void {
  const t = scene.add
    .text(640, y, text, {
      fontSize: '24px',
      color,
      fontStyle: 'bold',
      backgroundColor: '#0b0a12',
      padding: { x: 18, y: 12 },
      align: 'center',
    })
    .setOrigin(0.5)
    .setDepth(500)
    .setScrollFactor(0)
    .setName('creator-flash');
  scene.time.delayedCall(1800, () => t.destroy());
}

/**
 * A small picture of a project: its background, and its template's painted
 * map when shown. Returned as a container the caller positions.
 */
export function projectThumb(
  scene: Phaser.Scene,
  p: Pick<CreatorProject, 'background' | 'template' | 'showPicture'>,
  w: number,
  h: number,
): Phaser.GameObjects.Container {
  const c = scene.add.container(0, 0);
  const bg = findBackground(p.background);
  if (bg.key && scene.textures.exists(bg.key)) {
    c.add(scene.add.image(0, 0, bg.key).setDisplaySize(w, h));
  } else {
    c.add(scene.add.rectangle(0, 0, w, h, bg.color ?? 0xffffff));
  }
  const pic = projectPictureKey(p);
  if (pic && scene.textures.exists(pic)) c.add(scene.add.image(0, 0, pic).setDisplaySize(w, h));
  c.add(scene.add.rectangle(0, 0, w, h).setStrokeStyle(2, 0x000000, 0.5));
  return c;
}

/**
 * The whole map as it plays — background, picture, track, plots, every piece
 * (textboxes included) — drawn at 1280×720 and scaled into a w×h container.
 * The admin reviews exactly this before approving (spec 2026-11-02 §4.2).
 */
export function levelPreview(
  scene: Phaser.Scene,
  p: Pick<CreatorProject, 'background' | 'template' | 'showPicture' | 'track' | 'plots' | 'pieces'>,
  w: number,
): Phaser.GameObjects.Container {
  const c = scene.add.container(0, 0);
  const at = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    c.add(o);
    return o;
  };
  const bg = findBackground(p.background);
  if (bg.key && scene.textures.exists(bg.key)) {
    at(scene.add.image(0, 0, bg.key).setDisplaySize(1280, 720));
  } else {
    at(scene.add.rectangle(0, 0, 1280, 720, bg.color ?? 0xffffff));
  }
  const pic = projectPictureKey(p);
  if (pic && scene.textures.exists(pic)) at(scene.add.image(0, 0, pic).setDisplaySize(1280, 720));
  const ordered = p.pieces
    .map((pc, i) => ({ pc, i }))
    .filter(({ pc }) => pc.x >= -40 && pc.x <= 1320 && pc.y >= -40 && pc.y <= 760)
    .sort((a, b) => PIECE_DEPTH[a.pc.layer] - PIECE_DEPTH[b.pc.layer] || a.i - b.i);
  const track = findTrack(p.track);
  const g = scene.add.graphics();
  g.setPosition(-640, -360);
  drawTrailStyle(g, track.path, track.trackStyle);
  for (const lead of track.entrances ?? []) drawTrailStyle(g, lead, track.trackStyle);
  // Under-track pieces, the track, plots, then the rest.
  for (const { pc } of ordered.filter((o) => o.pc.layer === 'decor')) {
    at(makePieceImage(scene, { ...pc, x: pc.x - 640, y: pc.y - 360 }, { still: true }));
  }
  at(g);
  for (const q of p.plots) {
    if (scene.textures.exists('plot')) at(scene.add.image(q.x - 640, q.y - 360, 'plot'));
  }
  for (const { pc } of ordered.filter((o) => o.pc.layer !== 'decor')) {
    at(makePieceImage(scene, { ...pc, x: pc.x - 640, y: pc.y - 360 }, { still: true }));
  }
  at(scene.add.rectangle(0, 0, 1280, 720).setStrokeStyle(6, 0x000000, 0.6));
  c.setScale(w / 1280);
  return c;
}
